import { AIReview, AIReviewStatus, PromptType } from "common/models/types";
import type { FullReport } from "services/caringnoteApi";

/**
 * 파트너 열람 응답(FullReport) → 내부 도메인 타입 `AIReview` 어댑터 (계획 §3-1 · T1).
 *
 * 3단 뷰어(`AIReviewDetailTemplate`)는 `AIReview`를 받으므로, 파트너 응답을 그 형태로 변환한다.
 * `AIReview`는 내부 도메인 타입이고 파트너 응답은 그 **진부분집합**이라 `as AIReview` 단언을 쓴다.
 *
 * ⚠️ `patientSnap`은 **담는다**(2026-08-14). 팝업용은 "3단 뷰어 — 근거문헌 포함 상세"라 제외 규정이
 *    없다. 약사가 상담 중 보는 화면이라 나이·성별·질환·알레르기 없이는 판단이 안 되고, 케어링노트는
 *    그 정보를 이미 갖고 있다. (2026-08-31: 탑재용 embed도 `patientSnap`을 받아 환자정보·처방표를
 *    조건부로 보여주게 됐다 — CaringNoteEmbedPage. 단 embed는 이 어댑터를 경유하지 않는다.)
 *    내부 식별자(`id`·`userId`)는 **서버가 제거해** 보낸다.
 * ⚠️ `patientId`·`userId`·`memo`·`pinnedAt`·`feedbackId`는 여전히 비운다 — 내부 메타데이터다.
 * ⚠️ `content`는 내부 `reviewResult` **원문**(⑆·`#reference-`·`{DRP}`·섹션 마커 포함)을 그대로 받는다는
 *    백엔드 계약 전제다(계획 F1.1). 전처리하지 않아야 어드민 팝업과 **동일하게** 렌더된다.
 */
export function toPartnerReview(res: FullReport): AIReview {
  return {
    id: res.reviewId,
    reviewResult: res.content,
    // FullReport.references는 `Reference | null` — AIReview.referenceSnap(`Reference | undefined`)에 맞춰 정규화.
    referenceSnap: res.references ?? undefined,
    // 3단 뷰어의 환자 정보 패널·처방 테이블이 이 값을 읽는다. 없으면 패널이 빈 채로 뜬다.
    patientSnap: res.patientSnap ?? undefined,
    // 열람 URL은 분석 완료 후에만 발급된다(계획 §1-3) → 상태는 COMPLETED(400) 고정.
    status: AIReviewStatus.COMPLETED,
    // ⚠️ 하드코딩하지 않는다 — 백엔드 분석 프리셋(PARTNER_REVIEW_PROMPT_TYPE)이 정본이고,
    //    여기서 고정하면 백엔드가 프리셋을 바꿀 때 렌더 분기만 옛 값으로 남는다(2026-08-14).
    //    계약 §7의 "통합 약물관리" = cmm_2. 응답에 없으면(구버전) 복약지도로 떨어뜨린다.
    promptType: (res.promptType as PromptType) ?? PromptType.MEDICATION_COUNSELING,
  } as AIReview;
}
